import * as React from 'react';
import * as _ from 'underscore';

import { NestedProjectBasicInfo } from '@tableau/nested-project-explorer';
import {
  ButtonType,
  ButtonWidget,
  CheckBoxWithLabelWidget,
  TextAreaWidget,
  TextFieldWidget
} from '@tableau/widgets';
import { IPromise } from 'angular';
import { FormikProps, withFormik } from 'formik';
import ReactDOM = require('react-dom');
import { Formatters, Messages } from '../../codegen/Localize';
import { ButtonWithSpinner } from '../../common/react/components/ButtonWithSpinner';
import { DialogErrorAlert, DialogWarningAlert } from '../../common/react/components/dialogAlerts/DialogAlerts';
import { IGetWorkbooksResult } from '../../ts/ServerApi/requests/GetWorkbooksRequest';
import { ReactModalService, ReactModalServiceDialogProps } from '../../ts/services/ReactModalService';
import UploadWorkbookDialogHelper from '../helpers/UploadWorkbookDialogHelper';
import { sendUploadWorkbookDialogTelemetry } from '../helpers/UploadWorkbookTelemetryEvents';
import { UploadWorkbookStyles } from '../styles/UploadWorkbookStyles';
import { UploadWorkbookDialogOpts } from './ConnectedUploadWorkbookDialog';
import { FileDragAndDrop, preventDefaultDrag } from './FileDragAndDrop';
import { ProjectExplorerDropdown } from './form/ProjectExplorerDropdown';
import { ConfirmOverwriteDialogOpts } from './OverwriteConfirmDialog';

// Form Values
export interface UploadWorkbookDialogValues {
  file: File;
  name: string;
  projectId: string;
  description: string;
  showSheetsAsTabs: boolean;
  nameLength?: string; // Used to store name length errors
}

export interface UploadWorkbookDispatchProps {
  uploadWorkbookActionDispatch: (values: UploadWorkbookDialogValues, overwrite?: boolean) => void;
}

export interface UploadWorkbookExternalProps {
  snapshot?: UploadWorkbookDialogValues;
  parentElement?: React.RefObject<any>;
}

interface InternalUploadWorkbookDialogFormState {
  readonly isBusy: boolean;
  readonly willOverwriteWorkbook: boolean;
}

// Fancy typing to make formik working
export type UploadWorkbookDialogProps = ReactModalServiceDialogProps & UploadWorkbookDispatchProps & UploadWorkbookExternalProps;
export type InternalUploadWorkbookDialogFormProps = UploadWorkbookDialogProps & FormikProps<UploadWorkbookDialogValues>;

const MAX_NAME_LENGTH = 251;
const MAX_DESCRIPTION_LENGTH = 4000;
const MAX_FILE_SIZE_BYTES = 52428800;
const VALID_FILE_TYPES = ['.twb', '.twbx'];
const KEYPRESS_DEBOUNCE_MS = 250;

export const UploadWorkbookDialogVariables = {
  MAX_NAME_LENGTH,
  MAX_DESCRIPTION_LENGTH,
  MAX_FILE_SIZE_BYTES,
  KEYPRESS_DEBOUNCE_MS
};

export enum UploadWorkbookErrorEvent {
  ExceedMaxFileSize = 'ExceedMaxFileSize',
  UnsupportedFileExt = 'UnsupportedFileExtension',
  NoError = ''
}

export class InternalUploadWorkbookDialogForm extends React.Component<InternalUploadWorkbookDialogFormProps, InternalUploadWorkbookDialogFormState> {
  private fileInput: React.RefObject<HTMLInputElement>;
  private fileButtonLegacyRef: HTMLElement;
  private latestGetWorkbooksRequestTimestamp: number = 0;
  private validateWorkbookPromise: IPromise<IGetWorkbooksResult>;
  private isComponentMounted: boolean = true; // Considered a react antipattern, but this is to avoid cancellable promises

  constructor(props: InternalUploadWorkbookDialogFormProps) {
    super(props);
    this.fileInput = React.createRef();
    this.props.validateForm();
    this.state = {
      isBusy: false,
      willOverwriteWorkbook: false,
    };
  }

  public shouldComponentUpdate(nextProps: InternalUploadWorkbookDialogFormProps, nextState: InternalUploadWorkbookDialogFormState) {
    // Checks whether Formik values or errors have changed since last render
    return !(_.isEqual(this.props.values, nextProps.values) && _.isEqual(this.props.errors, nextProps.errors)
      && _.isEqual(this.state, nextState));
  }

  public componentWillUnmount() {
    this.isComponentMounted = false;

    if (this.props.parentElement) {
      this.props.parentElement.current.focus();
    }
  }

  public componentDidMount() {
    // DialogWidget already uses a setTimeout to focus on button widgets.
    // Having a timeout > 0 overrides this.
    setTimeout(() => this.fileButtonLegacyRef.focus(), 10);
  }

  private triggerFileInput = () => {
    this.fileInput.current.click();
  }

  private setFileButton = (component: ButtonWidget) => {
    this.fileButtonLegacyRef = ReactDOM.findDOMNode(component) as HTMLElement;
  }

  private handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>, source: 'DragDrop' | 'SelectButton') => {
    // Only set the file if a user has selected a file. They may have hit cancel
    if (event.target.files.length > 0) {
      // Call Formik setValues to set both file and name inputs
      const file: File = event.target.files[0];
      const name = UploadWorkbookDialogHelper.getFileNameOnly(file.name);
      this.validateWorkbookName(name);
      this.props.setValues({
        ...this.props.values,
        file,
        name
      });
      let errorEvent =  UploadWorkbookErrorEvent.NoError;
      if (file?.size > MAX_FILE_SIZE_BYTES) {
        errorEvent = UploadWorkbookErrorEvent.ExceedMaxFileSize;
      }
      if (!UploadWorkbookDialogHelper.isValidExtension(file.name, VALID_FILE_TYPES)) {
        errorEvent = UploadWorkbookErrorEvent.UnsupportedFileExt;
      }
      sendUploadWorkbookDialogTelemetry(source, file, errorEvent);
    }
  }

  // Need to convert shared widget handleChange to a standard HTMLEvent
  private handleShowSheetsCheckboxChange = (checked: boolean) => {
    return this.props.handleChange({
      target: {
        name: 'showSheetsAsTabs',
        value: checked
      }
    });
  }

  private getDescriptionText = () => {
    const characterCount = Formatters.formatCount(this.props.values.description.length);
    const maxDescriptionLength = Formatters.formatCount(MAX_DESCRIPTION_LENGTH);
    if (this.props.errors.description) {
      return Messages.contentUpload_workbookDescriptionError({
        CHARACTER_COUNT: characterCount,
        CHARACTER_LIMIT: maxDescriptionLength
      });
    } else {
      return Messages.characterCounter({
        COUNT_LOC: characterCount,
        MAXIMUM_LOC: maxDescriptionLength
      });
    }
  }

  private handleWorkbookOverwriteWithConfirmation() {
    ReactModalService.open(ConfirmOverwriteDialogOpts({
      workbookName: this.props.values.name,
      submitWorkbookUpload: () => {
        const overwrite = true;
        this.props.uploadWorkbookActionDispatch(this.props.values, overwrite);
      },
      openUploadWorkbookDialog: () => {
        ReactModalService.open(UploadWorkbookDialogOpts(this.props.values));
      }
    }));
  }

  private handleSubmit = () => {
    if (this.validateWorkbookPromise) {
      this.setState({ isBusy: true });
    }
    this.validateWorkbookPromise?.then((result: IGetWorkbooksResult) => {
      if (!this.isComponentMounted) {
        // Don't take action if the user has canceled or closed the dialog already
        return;
      }
      if (result?.totalCount > 0) {
        this.handleWorkbookOverwriteWithConfirmation();
      } else {
        this.props.uploadWorkbookActionDispatch(this.props.values);
      }
      this.props.close();
    });
  }

  private handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.props.handleChange(event);
    this.validateWorkbookName(event.currentTarget.value);
  }

  private validateWorkbookName = _.debounce((workbookName: string, projectId: string = this.props.values.projectId) => {
    if (workbookName) {
      // If the search query is slow, it's possible the user typed more and the second query
      // returned before the first. The timestamp keeps track of the last one.
      const requestTimestamp = Date.now();
      this.latestGetWorkbooksRequestTimestamp = requestTimestamp;

      this.validateWorkbookPromise = UploadWorkbookDialogHelper.getWorkbooksCall(workbookName, projectId);
      this.validateWorkbookPromise.then(result => {
        let willOverwriteWorkbook = false;
        if (result?.totalCount > 0 && requestTimestamp === this.latestGetWorkbooksRequestTimestamp) {
          willOverwriteWorkbook = true;
        }
        if (this.isComponentMounted) {
          // Don't set the state if the component was unmounted before the promise returned. This will throw a warning in the console
          this.setState({ willOverwriteWorkbook });
        }
      });
    } else {
      // Blank input scenario, clear the warning message
      this.setState({ willOverwriteWorkbook: false });
    }
  }, KEYPRESS_DEBOUNCE_MS);

  render() {
    return (
      <>
        <div className={UploadWorkbookStyles.contentContainer}>
          <div className={UploadWorkbookStyles.innerContentContainer}>
            <input
              data-tb-test-id="upload-workbook-input-file-id"
              type="file"
              name="file"
              ref={this.fileInput}
              style={{ display: 'none'}}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.handleFileSelection(event, 'SelectButton')}
              accept={VALID_FILE_TYPES.join(',')}
            />
            <FileDragAndDrop
              handleDrop={(event: React.ChangeEvent<HTMLInputElement>) => this.handleFileSelection(event, 'DragDrop')}
              containerClassName={UploadWorkbookStyles.dragAndDropContainer}
              defaultStateClassName={UploadWorkbookStyles.defaultOutline}
              activeStateClassName={UploadWorkbookStyles.activeOutline}
              onDragClassName={UploadWorkbookStyles.focusOutline}
              testId="upload-workbook-dialog-file"
            >
              <div className={UploadWorkbookStyles.dragAndDropChildren} onDragLeave={preventDefaultDrag}>
                {this.props.values.file &&
                  <b className={UploadWorkbookStyles.fileName}>{this.props.values.file.name}</b>
                }
                {!this.props.values.file &&
                  <>
                    <p className={UploadWorkbookStyles.dragAndDropText}>{Messages.contentUpload_dragAndDrop()}</p>
                    <p className={UploadWorkbookStyles.dragAndDropOrText}>{Messages.contentUpload_dragAndDropOr()}</p>{}
                  </>
                }
                <ButtonWidget
                  buttonType={ButtonType.Outline}
                  testId="upload-workbook-dialog-file"
                  children={Messages.contentUpload_chooseAFile()}
                  handleClick={this.triggerFileInput}
                  ref={this.setFileButton}
                  aria-describedby="upload-workbook-dialog-workbook-limit-message"
                />
              </div>
            </FileDragAndDrop>
            {this.props.errors.file && <DialogErrorAlert testId={'workbook-invalid-extension-alert'} message={this.props.errors.file as string} />}
            <p className={UploadWorkbookStyles.uploadWorkbookFileLimitDescription} id="upload-workbook-dialog-workbook-limit-message">
              {Messages.contentUpload_uploadWorkbookLimitMessage()}
            </p>
          </div>
          <div className={UploadWorkbookStyles.divider}/>
          <div className={`${UploadWorkbookStyles.workbookDetailsContainer} ${UploadWorkbookStyles.innerContentContainer}`}>
            <TextFieldWidget
              kind="line"
              name="name"
              testId="upload-workbook-dialog-name"
              value={this.props.values.name}
              label={Messages.name()}
              className={UploadWorkbookStyles.textField}
              onChange={this.handleNameChange}
              onClear={() => {
                this.props.setFieldValue('name', '');
                this.setState({ willOverwriteWorkbook: false });
              }}
              message={this.props.errors.nameLength}
              valid={this.props.errors.nameLength ? false : undefined}  // Only show error state
            />
            {this.state.willOverwriteWorkbook && <DialogWarningAlert testId={'workbook-overwrite-alert'} message={Messages.contentUpload_workbookNameOverwriteWarningMessage()}/>}

            <ProjectExplorerDropdown
              testId="upload-workbook-dialog-project"
              initialProjectId={this.props.values.projectId}
              handleProjectSelect={(project: NestedProjectBasicInfo) => {
                const projectId = project?.id || null;
                this.props.setFieldValue('projectId', projectId);
                this.validateWorkbookName(this.props.values.name, projectId);
              }}
            />
            <div>
              <TextAreaWidget
                name="description"
                label={Messages.contentUpload_uploadWorkbookDescriptionTitle()}
                testId="upload-workbook-dialog-description"
                value={this.props.values.description}
                className={`${UploadWorkbookStyles.textField} ${UploadWorkbookStyles.descriptionField}`}
                onChange={this.props.handleChange}
                valid={this.props.errors.description ? false : undefined}  // Undefined shows the normal focus
                message={this.getDescriptionText()}
              />
            </div>
            <div className={UploadWorkbookStyles.sheetsContainer}>
              <b className={UploadWorkbookStyles.sheetsLabel}>{Messages.sheets()}</b>
              <br/>
              <span>{Messages.all()}</span>
            </div>
            <CheckBoxWithLabelWidget
              name="showSheetsAsTabs"
              testId="upload-workbook-dialog-show-sheets-as-tabs"
              label={Messages.contentUpload_workbookShowSheets()}
              checked={this.props.values.showSheetsAsTabs}
              handleChange={this.handleShowSheetsCheckboxChange}
              removeLeftMargin={true}
            />
          </div>
        </div>
        <div className={UploadWorkbookStyles.footer}>
          <ButtonWidget
            buttonType={ButtonType.Outline}
            testId={'upload-workbook-dialog-cancel'}
            children={Messages.cancel()}
            handleClick={this.props.close}
          />
          <ButtonWithSpinner
            disabled={!this.props.isValid}
            buttonType={ButtonType.Primary}
            showSpinner={this.state.isBusy}
            testId={'upload-workbook-dialog-submit'}
            title={Messages.upload()}
            clicked={this.handleSubmit}
            hideTitleWithSpinner={true}
            onDarkBackground={true}
          />
        </div>
      </>
    );
  }
}

function validateFormFields(values: UploadWorkbookDialogValues) {
  const errors: any = {};
  // TODO: Actual error text will be handled in validation story TFS 1049117
  if (!values.name) {
    errors.nullName = 'no name';
  }

  if (values.name?.length > MAX_NAME_LENGTH) {
    const characterCount = Formatters.formatCount(values.name.length);
    const characterLimit = Formatters.formatCount(MAX_NAME_LENGTH);
    errors.nameLength = Messages.contentUpload_uploadWorkbookNameLimitMessage({
      CHARACTER_COUNT: characterCount,
      CHARACTER_LIMIT: characterLimit
    });
  }

  if (!values.file) {
    errors.nullFile = 'no file';  // We don't show an error for this, only disable submit
  } else {
    if (values.file?.size > MAX_FILE_SIZE_BYTES) {
      errors.file = Messages.contentUpload_workbookFileLimitError();
    }
    if (!UploadWorkbookDialogHelper.isValidExtension(values.file.name, VALID_FILE_TYPES)) {
      errors.file = Messages.contentUpload_workbookIncorrectExtensionError();
    }
  }

  if (!values.projectId) {
    errors.nullProjectId = 'no project id';
  }

  if (values.description?.length > MAX_DESCRIPTION_LENGTH) {
    errors.description = 'invalid description';  // message defined in getDescriptionText()
  }
  return errors;
}

export const UploadWorkbookDialog = withFormik<UploadWorkbookDialogProps, UploadWorkbookDialogValues>({
  mapPropsToValues: (props) => {
    return props.snapshot ?? {
      file: null,
      name: '',
      projectId: null,
      description: '',
      showSheetsAsTabs: false
    };
  },
  validate: (values) => {
    return validateFormFields(values);
  },
  handleSubmit: (values, actions) => {}
})(InternalUploadWorkbookDialogForm);

